import { createElement, useEffect, useRef, useState } from "react";
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
    Grid, IconButton, InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { Add, Badge, Delete, GroupAdd, Link as LinkIcon, People } from "@mui/icons-material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { getData, postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import EditableDataGrid from "../Shared/EditableDataGrid";
import ReferenceExpertActionsCell from "../references/ReferenceExpertActionsCell";
import { AvailableSearchItem, ProjectDetail, ProjectExpertItem, ProjectOptions, ProjectStaffItem, StaffDirectory } from "../../types/projectTypes";
import { numberOrNull } from "./projectUtils";

type ProjectTeamTabProps = {
    detail: ProjectDetail;
    options: ProjectOptions;
    isEditMode: boolean;
    setLoading: Function;
    refresh: () => Promise<void>;
};

const INTENSITY_LABELS = ['', 'Very Low', 'Low', 'Moderate', 'High', 'Very High'];

// ---------- Experts grid ----------

// `id` is the expertID (stable row key, used by ReferenceExpertActionsCell to link out),
// `linkID` is bridge_experts_projects.ID.
type ExpertRow = {
    id: number;
    linkID?: number;
    name: string;
    position: string;
    daysPlanned: number | string | null;
    daysWorked: number | string | null;
    ratePerDay: number | string | null;
    source: string;
    national: string;
    firm: string;
    remarks: string;
    cvlink: string | null;
};

const expertColumns: GridColDef[] = [
    { field: 'name', headerName: 'Expert', flex: 1.3, editable: false },
    { field: 'position', headerName: 'Position', flex: 1.2, editable: true },
    { field: 'daysPlanned', headerName: 'Days planned', flex: 0.7, editable: true, type: 'number' },
    { field: 'daysWorked', headerName: 'Days worked', flex: 0.7, editable: true, type: 'number' },
    { field: 'ratePerDay', headerName: 'Rate / Day', flex: 0.7, editable: true, type: 'number', description: 'Selling rate per day on this project.' },
    { field: 'source', headerName: 'Source', flex: 0.8, editable: true, type: 'singleSelect', valueOptions: ['Integration', 'Partner'],
        description: 'Engaged directly via Integration or via a partner.' },
    { field: 'national', headerName: 'National', flex: 0.6, editable: true, type: 'singleSelect', valueOptions: ['Yes', 'No'] },
    { field: 'firm', headerName: 'Firm', flex: 0.8, editable: true },
    { field: 'remarks', headerName: 'Remarks', flex: 1, editable: true },
    {
        field: 'actions', headerName: 'Links', width: 90, editable: false, sortable: false, filterable: false,
        renderCell: (params: GridRenderCellParams) => createElement(ReferenceExpertActionsCell, { row: params.row }),
    },
];

const toExpertRow = (e: ProjectExpertItem): ExpertRow => ({
    id: e.expertID,
    linkID: e.id,
    name: e.name,
    position: e.position,
    daysPlanned: e.daysPlanned,
    daysWorked: e.daysWorked,
    ratePerDay: e.ratePerDay,
    source: e.source,
    national: e.national === null ? '' : e.national ? 'Yes' : 'No',
    firm: e.firm,
    remarks: e.remarks,
    cvlink: e.cvlink,
});

const expertPayload = (row: ExpertRow) => ({
    position: row.position || null,
    daysPlanned: numberOrNull(row.daysPlanned),
    daysWorked: numberOrNull(row.daysWorked),
    ratePerDay: numberOrNull(row.ratePerDay),
    source: row.source || null,
    national: row.national === '' || row.national === undefined ? null : row.national === 'Yes',
    firm: row.firm || null,
    remarks: row.remarks || null,
});

// ---------- Bulk "add several experts" dialog ----------

function BulkAddExpertsDialog({ open, projectId, onClose, onAdded }: { open: boolean; projectId: number; onClose: () => void; onAdded: () => void }) {
    const notify = useNotification();
    const [selected, setSelected] = useState<AvailableSearchItem[]>([]);
    const [options, setOptions] = useState<AvailableSearchItem[]>([]);
    const [searching, setSearching] = useState(false);
    const [position, setPosition] = useState('');
    const [source, setSource] = useState('');
    const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!open) { setSelected([]); setOptions([]); setPosition(''); setSource(''); }
    }, [open]);

    const onInput = (_: unknown, value: string, reason: string) => {
        if (reason === 'reset') return;
        if (debounce.current) clearTimeout(debounce.current);
        if (value.trim().length < 2) { setOptions([]); return; }
        debounce.current = setTimeout(async () => {
            setSearching(true);
            try {
                setOptions(await getData(`projects/${projectId}/available-experts?search=${encodeURIComponent(value.trim())}`));
            } finally {
                setSearching(false);
            }
        }, 300);
    };

    const handleAdd = async () => {
        try {
            await postData('projects/experts/add', {
                projectId,
                expertIDs: selected.map((s) => s.id),
                position: position || undefined,
                source: source || undefined,
            });
            notify(`${selected.length} expert(s) added.`, 'success');
            onAdded();
            onClose();
        } catch (error) {
            notify(`Could not add experts: ${(error as Error).message}`, 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add several experts</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Search and pick as many experts as you like - they are added with the same position and source,
                    which you can refine per expert afterwards.
                </Typography>
                <Autocomplete
                    multiple
                    sx={{ marginTop: 2 }}
                    options={[...selected, ...options.filter((o) => !selected.some((s) => s.id === o.id))]}
                    value={selected}
                    filterOptions={(opts) => opts.filter((o) => !selected.some((s) => s.id === o.id))}
                    getOptionLabel={(o) => o.name + (o.email ? ` (${o.email})` : '')}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, value) => setSelected(value)}
                    onInputChange={onInput}
                    loading={searching}
                    noOptionsText="Type at least 2 characters"
                    renderTags={(value, getTagProps) => value.map((o, index) => <Chip size="small" label={o.name} {...getTagProps({ index })} key={o.id} />)}
                    renderInput={(params) => (
                        <TextField {...params} label="Experts" InputProps={{ ...params.InputProps, endAdornment: <>{searching ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />
                    )}
                />
                <Grid container spacing={2} sx={{ marginTop: 0.5 }}>
                    <Grid item xs={12} md={7}>
                        <TextField fullWidth label="Position (optional)" value={position} onChange={(e) => setPosition(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <FormControl fullWidth>
                            <InputLabel>Source (optional)</InputLabel>
                            <Select label="Source (optional)" value={source} onChange={(e) => setSource(e.target.value)}>
                                <MenuItem value="">-</MenuItem>
                                <MenuItem value="Integration">Integration</MenuItem>
                                <MenuItem value="Partner">Partner</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={!selected.length} onClick={handleAdd}>Add {selected.length || ''}</Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------- Internal staff ----------

type PersonOption = { oid: string | null; name: string; email: string; group: string };

function StaffDialog({ open, onClose, onSave, directory, roles, title, initialRole, initialIntensity }: {
    open: boolean; onClose: () => void; onSave: (person: PersonOption, role: string, intensity: number | null) => Promise<void>;
    directory: StaffDirectory | null; roles: string[]; title: string; initialRole?: string; initialIntensity?: number | null;
}) {
    const [person, setPerson] = useState<PersonOption | string | null>(null);
    const [role, setRole] = useState(initialRole || roles[0] || '');
    const [intensity, setIntensity] = useState<number | ''>('');

    useEffect(() => {
        if (open) { setPerson(null); setRole(initialRole || roles[0] || ''); setIntensity(initialIntensity ?? ''); }
    }, [open, initialRole, initialIntensity, roles]);

    const people: PersonOption[] = [
        ...(directory?.people || []).map((p) => ({ oid: p.oid, name: p.name, email: p.email, group: 'Entra ID staff group' })),
        ...(directory?.manualNames || []).map((name) => ({ oid: null, name, email: '', group: 'Used before (not linked)' })),
    ];

    const resolved: PersonOption | null = typeof person === 'string'
        ? (person.trim() ? { oid: null, name: person.trim(), email: '', group: '' } : null)
        : person;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {directory?.source === 'unavailable' && (
                    <Alert severity="info" sx={{ marginBottom: 2 }}>
                        The Entra ID staff group is not connected yet, so type the name. It can be linked to the user later.
                    </Alert>
                )}
                <Autocomplete
                    freeSolo
                    sx={{ marginTop: 1 }}
                    options={people}
                    groupBy={(o) => o.group}
                    value={person}
                    onChange={(_, value) => setPerson(value)}
                    onInputChange={(_, value, reason) => { if (reason === 'input') setPerson(value); }}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o.name + (o.email ? ` (${o.email})` : ''))}
                    renderInput={(params) => <TextField {...params} label="Person" />}
                />
                <Grid container spacing={2} sx={{ marginTop: 0.5 }}>
                    <Grid item xs={12} md={7}>
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
                                {roles.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <FormControl fullWidth>
                            <InputLabel>Intensity</InputLabel>
                            <Select label="Intensity" value={intensity} onChange={(e) => setIntensity(e.target.value as number | '')}>
                                <MenuItem value="">-</MenuItem>
                                {[1, 2, 3, 4, 5].map((i) => <MenuItem key={i} value={i}>{INTENSITY_LABELS[i]}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={!resolved || !role} onClick={() => resolved && onSave(resolved, role, intensity === '' ? null : intensity)}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ProjectTeamTab({ detail, options, isEditMode, setLoading, refresh }: ProjectTeamTabProps) {
    const notify = useNotification();
    const projectId = detail.project.ID;
    const [expertRows, setExpertRows] = useState<ExpertRow[]>(detail.experts.map(toExpertRow));
    const [bulkOpen, setBulkOpen] = useState(false);
    const [directory, setDirectory] = useState<StaffDirectory | null>(null);
    const [addStaffOpen, setAddStaffOpen] = useState(false);
    const [linkTarget, setLinkTarget] = useState<ProjectStaffItem | null>(null);

    useEffect(() => { setExpertRows(detail.experts.map(toExpertRow)); }, [detail.experts]);

    useEffect(() => {
        if (isEditMode && !directory) {
            getData('projects/staff/directory').then((res) => setDirectory(res as StaffDirectory)).catch((error) => console.error(error));
        }
    }, [isEditMode, directory]);

    const run = async (work: () => Promise<unknown>, success?: string) => {
        setLoading(true);
        try {
            await work();
            if (success) notify(success, 'success');
            await refresh();
        } catch (error) {
            notify((error as Error).message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // EditableDataGrid only changes local state; diff against the previous rows and call the API.
    const handleExpertsChange = async (wrapper: { expertRows: ExpertRow[] }) => {
        const previous = expertRows;
        const next = wrapper.expertRows || [];
        const additions = next.filter((row) => !previous.some((p) => p.id === row.id));
        const removals = previous.filter((p) => !next.some((row) => row.id === p.id));
        const updates = next.filter((row) => {
            const before = previous.find((p) => p.id === row.id);
            return before && before.linkID !== undefined && JSON.stringify(expertPayload(before)) !== JSON.stringify(expertPayload(row));
        });
        await run(async () => {
            for (const row of removals) if (row.linkID !== undefined) await postData('projects/experts/remove', { id: row.linkID });
            for (const row of additions) await postData('projects/experts/add', { projectId, expertIDs: [row.id], ...expertPayload(row) });
            for (const row of updates) await postData('projects/experts/update', { id: row.linkID, ...expertPayload(row) });
        });
    };

    const searchExperts = async (query: string) =>
        getData(`projects/${projectId}/available-experts?search=${encodeURIComponent(query)}`);

    const addStaff = async (person: PersonOption, role: string, intensity: number | null) => {
        await run(() => postData('projects/staff/add', {
            projectId, staff_oid: person.oid, staff_name: person.name, staff_email: person.email || null, role, intensity,
        }), `${person.name} added as ${role}.`);
        setAddStaffOpen(false);
    };

    const linkStaff = async (person: PersonOption, role: string, intensity: number | null) => {
        if (!linkTarget) return;
        await run(() => postData('projects/staff/update', {
            id: linkTarget.id, staff_oid: person.oid, staff_name: person.name, staff_email: person.email || null, role, intensity,
        }), 'Staff entry updated.');
        setLinkTarget(null);
    };

    return (
        <Box paddingTop={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Stack direction="row" alignItems="center" gap={1}><Badge /><Typography variant="h6">Internal team</Typography></Stack>
                {isEditMode && <Button size="small" variant="contained" startIcon={<Add />} onClick={() => setAddStaffOpen(true)}>Add person</Button>}
            </Stack>
            {detail.staff.length === 0 ? (
                <Typography color="text.secondary" sx={{ paddingY: 2 }}>No internal staff assigned yet.</Typography>
            ) : (
                <Table size="small" sx={{ marginTop: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Role</TableCell>
                            <TableCell>Person</TableCell>
                            <TableCell>Intensity</TableCell>
                            {isEditMode && <TableCell align="right" />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {detail.staff.map((s) => (
                            <TableRow key={s.id}>
                                <TableCell>{s.role}</TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <span>{s.staff_name}</span>
                                        {s.staff_email && <Typography variant="caption" color="text.secondary">{s.staff_email}</Typography>}
                                        {!s.linked && (
                                            <Tooltip title="Entered by name only - not linked to an Entra ID user yet">
                                                <Chip size="small" variant="outlined" label="not linked" />
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell>{s.intensity ? INTENSITY_LABELS[s.intensity] : '-'}</TableCell>
                                {isEditMode && (
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title={s.linked ? 'Change person / role' : 'Link to Entra ID user'}>
                                            <IconButton size="small" onClick={() => setLinkTarget(s)}><LinkIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove">
                                            <IconButton size="small" color="error"
                                                onClick={() => window.confirm(`Remove ${s.staff_name} (${s.role})?`) && run(() => postData('projects/staff/remove', { id: s.id }))}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ marginTop: 4, marginBottom: 1 }}>
                <Stack direction="row" alignItems="center" gap={1}><People /><Typography variant="h6">Experts</Typography></Stack>
                {isEditMode && <Button size="small" variant="outlined" startIcon={<GroupAdd />} onClick={() => setBulkOpen(true)}>Add several experts</Button>}
            </Stack>
            <EditableDataGrid
                col={expertColumns}
                rows={expertRows}
                editIsOn={isEditMode}
                formData={{ expertRows }}
                setFormData={handleExpertsChange}
                dataPath="expertRows"
                preselectionIsOn
                preselectionDisplayField="name"
                preselectionValueField="id"
                readOnlyFields={['name']}
                preselectionSearchFn={searchExperts}
                preselectionSearchLabel="Search for an expert by name or email"
            />

            <BulkAddExpertsDialog open={bulkOpen} projectId={projectId} onClose={() => setBulkOpen(false)} onAdded={() => refresh()} />
            <StaffDialog open={addStaffOpen} onClose={() => setAddStaffOpen(false)} onSave={addStaff} directory={directory}
                roles={options.staffRoles} title="Add person to the internal team" />
            <StaffDialog open={!!linkTarget} onClose={() => setLinkTarget(null)} onSave={linkStaff} directory={directory}
                roles={options.staffRoles} initialRole={linkTarget?.role} initialIntensity={linkTarget?.intensity} title={`Update ${linkTarget?.staff_name ?? ''}`} />
        </Box>
    );
}

export default ProjectTeamTab;
